import { CanvasManager } from '@Components/BigBrowse/CanvasManager/CanvasManager';
import { getMultiSectionLayout } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/getMultiSectionLayout';
import { getSingleSectionLeftGridLayout } from '@Components/BigBrowse/Views/DetailViews/SingleSectionView/singleSectionRenderer';
import type { FloorContentDM, FloorLayout } from '@Model/FloorModels';
import { getDestinationSignature } from '@Navigator2/urlUtils';
import { waitForMs } from '@Utils/miscUtils';
import type { TransientClass } from '@Utils/useClassStore';
import { ObservableProp } from '@Utils/useClassStore';
import { inject, injectable, postConstruct } from 'inversify';
import type { IReactionDisposer } from 'mobx';
import { action, computed, observable, reaction, toJS, when } from 'mobx';
import { createContext, useContext } from 'react';

import {
  getExplodingPositionDeltas,
  rectanglesOverlap,
} from '../BBCalculationsUtils';
import {
  CAMERA_DEFAULT_Z,
  CAMERA_MAX_ZOOM,
  ZOOM_SPAN_SEGMENT,
} from '../BigBrowseConfig';
import type { GridCard } from '../Model/Classes/GridCard';
import type {
  CameraProps,
  GridCardData,
  GridLayout,
  ItemTransform,
  PlaneBoundaries,
  PlaneBox,
  Position2D,
} from '../Model/ModelTypes';
import { PlaneTypes } from '../Model/ModelTypes';
import type {
  BrandConcessionContentDM,
  ProductSetContentDM,
} from '../Views/DetailViews/MultiSectionView/common/MultiSectionView.types';
import {
  getTargetHeroSpecs,
  USE_MULTI_SECTION_FOR_PRODUCT_SET,
} from '../Views/DetailViews/getTragetHeroSpecs';
import { getFloorCellLayout } from '../Views/FloorCellView/FloorCellView';
import { BbContentData } from './BbContentData';
import { BbQueueManager } from './BbQueueManager';
import type { DestinationTitle } from './DestinationTitlesRegistry';
import { DestinationTitlesRegistry } from './DestinationTitlesRegistry';
import { HeroLoadingTracker } from './HeroLoadingTracker';

import container from '~ioc/ioc';
import {
  AnalyticsStore,
  extractViewBrandConcessionAnalyticsData,
  extractViewBrandSectionAnalyticsData,
  extractViewProductSetAnalyticsData,
} from '~stores/analytics';
import AuthStore from '~stores/authentication';
import { FloorsListStore } from '~stores/floors-list';
import { NavigationStore } from '~stores/navigation';
import type { AnalyticsEventData } from '~types/analytics';
import type { JourneyParameters } from '~types/navigation';
import type { ZoomSpan } from '~types/views-types';

const DISABLE_SCROLL_BOUNDARIES = false;

type BackButtonView = {
  key: string;
  title: string;
  onClick: () => void;
  darkMode?: boolean;
};

function destinationDtoToJourneyForProduct(
  item: GridCard<PlaneTypes.Card_ShopFloor_Product>,
  hero: GridCard<PlaneTypes>
): JourneyParameters {
  return {
    journey: 'brandconcession',
    journeyParam: Number(hero.data!.id),
    productId: Number(item.data!.id),
  };
}

export function getZoomSpanForZoom(zoom: number): ZoomSpan {
  if (zoom >= CAMERA_MAX_ZOOM - ZOOM_SPAN_SEGMENT) {
    return 'span0';
  }

  if (zoom >= CAMERA_MAX_ZOOM - ZOOM_SPAN_SEGMENT * 2) {
    return 'span1';
  }

  if (zoom >= CAMERA_MAX_ZOOM - ZOOM_SPAN_SEGMENT * 3) {
    return 'span2';
  }

  return 'span3';
}

export function getEffectForZoomSpan(zoomSpan: ZoomSpan): {
  collapse: boolean;
  noBackground: boolean;
} {
  switch (zoomSpan) {
    case 'span0':
      return { collapse: false, noBackground: false };
    case 'span1':
      return { collapse: false, noBackground: false };
    case 'span2':
      return { collapse: true, noBackground: false };
    case 'span3':
      return { collapse: true, noBackground: true };
    default:
      return { collapse: false, noBackground: false };
  }
}

@injectable()
export class BbContentPresenter implements TransientClass {
  @inject(CanvasManager) private _canvasManager!: CanvasManager;
  @inject(BbContentData) private _bbContentData!: BbContentData;
  @inject(NavigationStore) private _navigator2Store!: NavigationStore;
  @inject(HeroLoadingTracker) private _heroLoadingTracker!: HeroLoadingTracker;
  @inject(BbQueueManager) private _bbQueueManager!: BbQueueManager;
  @inject(DestinationTitlesRegistry)
  private _destinationTitleRegistry!: DestinationTitlesRegistry;
  @inject(AnalyticsStore) private _analyticsStore!: AnalyticsStore;
  @inject(AuthStore) private _authStore!: AuthStore;
  @inject(FloorsListStore) private _floorsStore2!: FloorsListStore;

  visible = new ObservableProp(true);
  routeKey = new ObservableProp('');
  animating = new ObservableProp(false);
  ready = new ObservableProp(false);
  heroesLoaded = observable.set<string>(); // stores the 'key' of the hero cards that has loaded images

  currentZoomSpan = new ObservableProp<ZoomSpan>(
    getZoomSpanForZoom(CAMERA_MAX_ZOOM)
  );
  enableCollapsedDisplaysFX = new ObservableProp(false);
  enableSuppressHeroBackgroundFX = new ObservableProp(false);
  isExploded = new ObservableProp(false);

  private _scrollAtLaunch = new ObservableProp(true);
  private _layout = new ObservableProp<GridLayout | undefined>(undefined);
  private _reactionDisposers: IReactionDisposer[] = [];
  private _viewedSectionIds = observable.set<number>();

  @postConstruct() postConstruct() {
    this._canvasManager.init({
      scrollBoundaryProvider: this,
    });

    this._reactionDisposers.push(
      reaction(
        () => [
          this._bbContentData.journeyParams.value?.journey,
          this._bbContentData.journeyParams.value?.journeyParam,
          this.canvasManager.windowSize.value,
          this.data,
          this._bbContentData.floorLayout,
          this.displayHeroOnlyLayout,
        ],
        async () => {
          if (
            !this.canvasManager.windowSize.value.width ||
            !this.canvasManager.windowSize.value.height ||
            !this.data
          ) {
            // If one of the above values is not ready, don't do anything
            // wait for all values to be ready to calculate the layout
            return;
          }

          const layout = await this.getLayoutCalculation();

          this._layout.set(layout);
        },
        { fireImmediately: true }
      ),

      // update state for FX to be applied bsed on zoom level
      reaction(
        () => this.cameraProps.zoom,
        () => {
          const previousZoomSpan = this.currentZoomSpan.value;
          this.currentZoomSpan.set(getZoomSpanForZoom(this.cameraProps.zoom));
          if (previousZoomSpan !== this.currentZoomSpan.value) {
            const { collapse, noBackground } = getEffectForZoomSpan(
              this.currentZoomSpan.value
            );
            this.enableCollapsedDisplaysFX.set(collapse);
            this.enableSuppressHeroBackgroundFX.set(noBackground);
          }
        },
        { fireImmediately: true }
      ),

      // Refocus map after the layout changes (collapse-expands).
      reaction(
        () => this.enableCollapsedDisplaysFX.value,
        async () => {
          const currentPosition = this.cameraProps.position;
          const currentSize = this.layout?.size;

          if (!currentSize) {
            // if we launch on small screen, then we immediately collapse the displays
            // this means that the layout size is not yet ready, so we need to wait for it
            // and perform the re-focus action
            return;
          }

          // wait for the new layout to kick-in
          await when(
            () =>
              this.layout?.size.height !== currentSize?.height &&
              this.layout?.size?.width !== currentSize?.width
          );

          const newSize = this.layout?.size;

          // the new focus point is proportional to the previous focus point based on layout size
          const newX =
            (currentPosition.x * newSize!.width) / currentSize!.width;
          const newY =
            (currentPosition.y * newSize!.height) / currentSize!.height;

          await waitForMs(100); // need to escape from the current cycle in order for the animation to work
          this.canvasManager.scrollTo([newX, newY, CAMERA_DEFAULT_Z], true);
        }
      )
    );

    this.logAnalyticsOnCreate().then();
    this.monitorScrolledSectionsOnMultiSection().then();

    this.updateCanvasViewOnWithViewType().then();
  }

  private async updateCanvasViewOnWithViewType() {
    // we need to wait for the journey params to be ready to determine the initial zoom level
    await when(() => this.journeyParams?.journey !== undefined, {
      timeout: 5000,
    });

    this.canvasManager.setIsAttachedToFloorView(this.isFloorView).then();
  }

  itemTransforms = observable.map<string, ItemTransform>({});

  @computed get journeyParams(): JourneyParameters | undefined {
    return this._bbContentData.journeyParams.value;
  }

  @computed get paramsSignature(): string {
    return this.journeyParams
      ? getDestinationSignature(this.journeyParams)
      : '';
  }

  @computed get viewTitle(): string {
    const floorId = this.journeyParams?.journeyParam;

    const floor = this._floorsStore2.floorsDM.value.find(
      (floorDM) => floorDM.id === Number(floorId)
    );

    return floor?.title ?? '???';
  }

  @computed get floorDM() {
    const floorId = this.journeyParams?.journeyParam;

    return this._floorsStore2.floorsDM.value.find(
      (floorDM) => floorDM.id === Number(floorId)
    );
  }

  @computed get isFloorView() {
    return this.journeyParams?.journey === 'floor';
  }

  @computed get isDesktopZoomEnabled() {
    return this.isFloorView;
  }

  get canvasManager(): CanvasManager {
    return this._canvasManager;
  }

  @computed private get data() {
    return this._bbContentData.data.value;
  }

  @computed get departmentName() {
    if (!this.data) {
      return '';
    }

    return (
      (this.data as BrandConcessionContentDM | ProductSetContentDM)
        ?.departmentName ?? ''
    );
  }

  @computed get departmentInfo() {
    return this.data as BrandConcessionContentDM | ProductSetContentDM;
  }

  private async getLayoutCalculation(): Promise<GridLayout> {
    if (!this.data) {
      return { size: { width: 0, height: 0 }, items: [] };
    }
    if (this.floorLayout !== undefined) {
      // @ts-expect-error
      return getFloorCellLayout(
        this.data as FloorContentDM,
        this.cameraProps,
        this.enableCollapsedDisplaysFX.value
      );
    }

    if (
      this._bbContentData.journeyParams.value?.journey === 'brandconcession' ||
      (this._bbContentData.journeyParams.value?.journey === 'productset' &&
        USE_MULTI_SECTION_FOR_PRODUCT_SET)
    ) {
      return getMultiSectionLayout(
        this.data as BrandConcessionContentDM,
        this.cameraProps
      );
    }

    if (this._bbContentData.journeyParams.value?.journey === 'productset') {
      const { gridSizePx, items, scrollBox } = getSingleSectionLeftGridLayout({
        data: this.data as ProductSetContentDM,
        cameraBox: this.cameraProps.box,
      });

      return { size: gridSizePx, items, scrollBox };
    }

    return { size: { width: 0, height: 0 }, items: [] };
  }

  @computed get layout() {
    return this._layout.value;
  }

  @computed get floorLayout(): FloorLayout | undefined {
    return this._bbContentData.floorLayout;
  }

  @computed get mountItems(): GridCard<PlaneTypes>[] {
    return (this.layout?.items ?? []).filter((mesh) =>
      rectanglesOverlap(this.canvasManager.cameraBoundaries, mesh.worldBox)
    );
  }

  @computed get scrollBoundary(): PlaneBoundaries | undefined {
    if (DISABLE_SCROLL_BOUNDARIES || !this.layout?.items) {
      return undefined;
    }

    const scrollLimits: PlaneBoundaries = this.layout.scrollBox ?? {
      left: -this.layout.size.width / 2,
      right: this.layout.size.width / 2,
      top: this.layout.size.height / 2,
      bottom: -this.layout.size.height / 2,
    };

    const result = {
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
    };

    // if screen side is larger than layout side then lock the scroll position to 0
    // otherwise scroll should be allowed for the center of screen up to such position
    // that screen edge is aligned with layout edge.
    // e.g. left of screen is at the left of layout

    if (this.layout.size.width > this.canvasManager.windowSize.value.width) {
      result.left =
        scrollLimits.left + this.canvasManager.windowSize.value.width / 2;
      result.right =
        scrollLimits.right - this.canvasManager.windowSize.value.width / 2;
    }

    if (this.layout.size.height > this.canvasManager.windowSize.value.height) {
      result.top =
        scrollLimits.top - this.canvasManager.windowSize.value.height / 2;
      result.bottom =
        scrollLimits.bottom + this.canvasManager.windowSize.value.height / 2;
    }

    return result;
  }

  @computed get showWelcomeMessage(): boolean {
    return this.journeyParams?.journey === 'floor';
  }

  @computed get isInitializing(): boolean {
    return this._bbContentData.isFetching.value;
  }

  @computed get showBottomNav(): boolean {
    return this.journeyParams?.journey !== 'floor';
  }

  @computed private get shopFloorDestinationTitles(): DestinationTitle[] {
    const { root } = this._bbQueueManager;
    if (!root) {
      return [];
    }

    return (
      root.layout?.items
        .filter((card) => card.type === PlaneTypes.Card_ShopFloor_Hero)
        .map((card) => ({
          // @ts-expect-error
          title: card.data?.title ?? '',
          destinationSignature: getDestinationSignature(
            // @ts-expect-error
            destinationDtoToJourneyParameters(card.data?.destination!)
          ),
        })) ?? []
    );
  }

  @computed get floorHasFocus() {
    if (this._bbQueueManager.stack.length === 1) {
      const [topFloor] = this._bbQueueManager.stack;
      const currentFloorId = this.journeyParams?.journeyParam;
      return (
        (topFloor.params as JourneyParameters)?.journeyParam === currentFloorId
      );
    }

    return false;
  }

  @computed get buttonsViews(): BackButtonView[] {
    const result: BackButtonView[] = [];

    if (this._bbQueueManager.stack.length === 0) {
      return [];
    }

    const presenter = this._bbQueueManager.root;

    result.push({
      title: presenter?.floorFocusTitle ?? '',
      key: presenter?.routeKey.value ?? '',
      darkMode: true,
      onClick: () => this.popToRoot(),
    });

    if (this._bbQueueManager.stack.length > 2) {
      const previousKey =
        this._bbQueueManager.stack[this._bbQueueManager.stack.length - 2]?.key;
      const previousPresenter =
        this._bbQueueManager.presenters.get(previousKey);

      let title = previousPresenter?.departmentName;
      if (!title) {
        const hero = this.shopFloorDestinationTitles.find(
          (card) =>
            card.destinationSignature === previousPresenter?.paramsSignature
        );

        title = hero?.title;
      }

      result.push({
        title: title ?? '',
        key: previousPresenter?.routeKey.value ?? '',
        darkMode: false,
        onClick: () => this.pop(previousPresenter!),
      });
    }

    return result;
  }

  @computed private get shopFloorDepartments() {
    const { root } = this._bbQueueManager;
    if (root?.floorLayout !== 'BrandConcession') {
      return [];
    }

    return (
      root.layout?.subGrids?.filter(
        // @ts-expect-error
        (grid) => grid.type === PlaneTypes.Grid_ShopFloor_Department
      ) ?? []
    );
  }

  @computed get activeDepartment() {
    const { x, y } = this.canvasManager.cameraPosition;
    return this.shopFloorDepartments.find((dep) =>
      rectanglesOverlap(dep.worldBox, {
        top: y,
        bottom: y,
        left: x,
        right: x,
      })
    );
  }

  @computed get cameraBoundaries() {
    return this.canvasManager.cameraBoundaries;
  }

  @computed get currentPosition() {
    const { x, y } = this.canvasManager.cameraPosition;

    return { x, y, cameraBoundary: this.canvasManager.cameraBoundaries };
  }

  @computed get fetchError(): string | undefined {
    return this._bbContentData.fetchError;
  }

  @computed private get cameraProps(): CameraProps {
    return {
      size: this.canvasManager.windowSize.value,
      position: this.canvasManager.cameraPosition,
      box: this.canvasManager.cameraBoundaries,
      zoom: this.canvasManager.cameraZoom.value,
    };
  }

  @computed private get floorFocusTitle() {
    const cameraCenterBox: PlaneBox = {
      top: this.cameraProps.position.y,
      bottom: this.cameraProps.position.y,
      left: this.cameraProps.position.x,
      right: this.cameraProps.position.x,
    };

    const focusedDepartment = this.layout?.displays?.find((display) =>
      rectanglesOverlap(display.box, cameraCenterBox)
    );

    if (!focusedDepartment) {
      return this.data?.floorName;
    }

    const departmentDM = this.floorDM?.departments.find(
      (dep) => dep.departmentId === focusedDepartment.model.dm.departmentId
    );

    return departmentDM?.name;
  }

  popToRoot = async () => {
    this.visible.set(false);
    this._bbQueueManager.root?.visible.set(true);
    await waitForMs(500);

    this._navigator2Store.popToRoot();
  };

  private pop = async (previousPresenter: BbContentPresenter) => {
    this.visible.set(false);
    previousPresenter.visible.set(true);
    await waitForMs(500);

    this._navigator2Store.pop();
  };

  private doInitialScroll = () => {
    if (this.layout?.modelData === undefined) {
      this._doInitialScrollDetailedView();
      return;
    }

    if (this.journeyParams?.depId) {
      return this.scrollToDepartmentFocusPoint(this.journeyParams.depId);
    }

    return this.scrollToRandomDepartment();
  };

  openProductPanel = (productId?: number) => {
    this._navigator2Store.navToProductId(productId).then();
  };

  onCardClick = async (item: GridCard<PlaneTypes>) => {
    if (
      [
        'Card_BigBrowse_Product',
        'Card_SingleSectionView_Product',
        'Card_MultiSection_Product',
      ].includes(item.type)
    ) {
      this.openProductPanel(Number(item.data?.id));
      return;
    }

    if (
      [
        'Card_BigBrowse_Bookend',
        'Card_ShopFloor_Hero',
        'Card_ShopFloor_Product',
        'Card_MultiSection_Footer_Item',
        'Card_MultiSection_Footer_Item_Small',
      ].includes(item.type)
    ) {
      const [explodedHeroBox, hero] = await this.explodeShop(item);

      const destination =
        item.type === PlaneTypes.Card_ShopFloor_Product
          ? destinationDtoToJourneyForProduct(
              item as GridCard<PlaneTypes.Card_ShopFloor_Product>,
              hero
            )
          : // @ts-expect-error
            item.data?.destination!;

      await this._navigator2Store.pushToDetailView(destination);

      // register detailViewTitle
      const destinationSignature = getDestinationSignature(destination);
      this._destinationTitleRegistry.titles.set(this.routeKey.value, {
        destinationSignature,
        title:
          (hero.data as GridCardData<PlaneTypes.Card_BigBrowse_Bookend>)
            ?.title ?? '',
      });

      // if exploded hero remains in screen then wait for target image to load
      if (
        rectanglesOverlap(this.canvasManager.cameraBoundaries, explodedHeroBox)
      ) {
        try {
          // wait for new screen hero image to load before hiding this plane

          await when(
            () =>
              Boolean(
                this._heroLoadingTracker.loaded.get(destinationSignature)
              ),
            { timeout: 4000 }
          );
        } catch {}
      }

      // make sure that the current floor does not have the focus before
      // hiding it

      if (!this.floorHasFocus) {
        this.hidePlane().then();
      }
    }
  };

  private _doInitialScrollDetailedView = () => {
    // this dummy scroll here makes solves an issue where text does not
    // render at all on page display, until you force it to update (with a scroll)
    this.canvasManager.truckByDelta(0, 1, true).then();
  };

  setScrollAtLaunch = (value: boolean) => {
    this._scrollAtLaunch.set(value);
  };

  init = async (
    journeyParams: JourneyParameters,
    routeKey: string,
    scrollAtLaunch = true
  ) => {
    this.routeKey.set(routeKey);
    this._bbContentData.init(journeyParams);
    this._scrollAtLaunch.set(scrollAtLaunch);
    await when(() => (this.layout?.items ?? []).length > 0, {
      timeout: 5000,
      name: 'Wait for layout initializer',
    });
    await waitForMs(1000);
    this._scrollAtLaunch.value && this.doInitialScroll();

    this.ready.set(true);
  };

  destroy = () => {
    this._reactionDisposers.forEach((disposer) => {
      disposer();
    });
    this._heroLoadingTracker.loaded.delete(this.paramsSignature);
    this._bbQueueManager.unregisterPresenter(this.routeKey.value);
    this._destinationTitleRegistry.titles.delete(this.routeKey.value);
  };

  implode = () => {
    this.itemTransforms.clear();
    this.isExploded.set(false);
  };

  private explodeShop = async (
    item: GridCard<PlaneTypes>
  ): Promise<[PlaneBoundaries, GridCard<PlaneTypes>]> => {
    this.isExploded.set(true);
    this.animating.set(true);
    const hero =
      item.type === PlaneTypes.Card_ShopFloor_Product ? item.parentHero! : item;

    const { size, worldPosition: targetPosition } = getTargetHeroSpecs(
      // @ts-expect-error
      item.data?.destination!,
      this.cameraProps,
      hero
    );

    const [dx, dy] = calculatePositionDelta(targetPosition, hero.worldPosition);
    const targetScale = size.width / hero.size.width;

    this.itemTransforms.set(hero.key!, {
      position: { to: { x: dx, y: dy } },
      scale: { to: targetScale },
    });

    this.mountItems.forEach((card) => {
      if (card !== hero) {
        const { dx: explodingDx, dy: explodingDy } = getExplodingPositionDeltas(
          this.canvasManager.cameraBoundaries,
          card.worldBox
        );

        this.itemTransforms.set(card.key!, {
          position: { to: { x: explodingDx, y: explodingDy } },
        });
      }
    });

    await waitForMs(1000);
    this.animating.set(false);

    // return exploded hero's target Boundaries
    return [
      {
        top: targetPosition.y + size.height / 2,
        bottom: targetPosition.y - size.height / 2,
        left: targetPosition.x - size.width / 2,
        right: targetPosition.x + size.width / 2,
      },
      hero,
    ];
  };

  @action hidePlane = async () => {
    this.visible.set(false);
  };

  onRefocus = () => {
    this.visible.set(true);
    this.implode();
  };

  onBlur = () => {
    this._heroLoadingTracker.loaded.delete(this.paramsSignature);
  };

  onFocus = () => {
    this.visible.set(true);
  };

  private logAnalyticsOnCreate = async () => {
    await when(() => Boolean(this._bbContentData.data.value), {
      timeout: 10000,
    });

    if (this.journeyParams?.journey === 'brandconcession') {
      const data = this._bbContentData.data.value as BrandConcessionContentDM;
      const logData: AnalyticsEventData<'view_brand_concession'> = {
        ...extractViewBrandConcessionAnalyticsData(data),
        userId: this._authStore.currentUser?.id,
        productId: this.journeyParams.productId,
      };

      this._analyticsStore.logViewBrandConcession(logData);
      return;
    }

    if (this.journeyParams?.journey === 'productset') {
      const data = this._bbContentData.data.value as ProductSetContentDM;
      const logData: AnalyticsEventData<'view_product_set'> = {
        ...extractViewProductSetAnalyticsData(data),
        userId: this._authStore.currentUser?.id,
        productId: this.journeyParams.productId,
      };
      this._analyticsStore.logViewProductSet(logData);
    }
  };

  private monitorScrolledSectionsOnMultiSection = async () => {
    // eslint-disable-next-line no-unreachable
    await when(() => Boolean(this.journeyParams?.journey), { timeout: 10000 });

    if (this.journeyParams?.journey === 'brandconcession') {
      this._reactionDisposers.push(
        reaction(
          () => this.mountItems,
          (mountItems) => {
            // get sectionId of all displayed data that have sectionId
            const visibleSectionIds = mountItems
              .filter((card) => Boolean(card.data?.sectionId))
              .map((card) => card.data!.sectionId!);

            // check if all these headers have been already viewed
            visibleSectionIds.forEach((id) => {
              const viewed = this._viewedSectionIds.has(id);

              if (!viewed) {
                this._viewedSectionIds.add(id);
                const journeyData = this._bbContentData.data
                  .value as BrandConcessionContentDM;

                const logData: AnalyticsEventData<'view_brand_section'> = {
                  ...extractViewBrandSectionAnalyticsData(journeyData, id),
                  userId: this._authStore.currentUser?.id,
                };

                this._analyticsStore.logViewBrandSection(logData);
              }
            });
          },
          { fireImmediately: true }
        )
      );
    }
  };

  @action scrollToDepartmentFocusPoint = async (departmentId: number) => {
    const position = this.layout?.scrollTargets?.[departmentId];

    if (position) {
      await waitForMs(500);
      this.canvasManager.scrollTo(
        [position.x, position.y, CAMERA_DEFAULT_Z],
        true
      );
    }
  };

  private scrollToRandomDepartment() {
    const departmentIds = Object.keys(this.layout?.scrollTargets ?? {});
    const randomDepartmentId =
      departmentIds[Math.floor(Math.random() * departmentIds.length)];

    return this.scrollToDepartmentFocusPoint(Number(randomDepartmentId));
  }

  @computed private get displayHeroOnlyLayout() {
    // don't run the following directly into reaction criteria. We need the result cached.
    // Otherwise it re-triggers reaction on each cameraZoom value change;
    return this.enableCollapsedDisplaysFX.value;
  }
}

container.bind(BbContentPresenter).toSelf().inTransientScope();

export const BbContentPresenterContext = createContext<
  BbContentPresenter | undefined
>(undefined);

export const useBbContentPresenter = () => {
  const context = useContext(BbContentPresenterContext);

  if (!context) {
    throw new Error('No context value found for BbContentPresenter');
  }

  return context;
};

const calculatePositionDelta = (
  finish: Position2D,
  start: Position2D
): [number, number] => [finish.x - start.x, finish.y - start.y];
